
import { AsyncTask, AsyncTaskFilters, AsyncTaskStatus, AsyncTaskType } from '@/types/catalogs/AsyncTask'
import AsyncTaskNotificationSection from './AsynTaskNotificationSection.vue'
import AsyncTaskNotificationCard from './AsyncTaskNotificationCard.vue'
import Vue, { VNode } from 'vue'
import CatalogsFillsService from '@/services/CatalogsFillsService'
import userInfo from '@/store/userInfo'
import { NUMBER } from '@/types/GeneralConstants'
import { PermissionNames } from '@/types/Auth'
import LogService from '@/services/support/LogService'
import { PageRequest, SortDirection } from '@/types/Support'
import { mapActions, mapMutations, mapGetters } from 'vuex'

export default Vue.extend({
  components: {
    AsyncTaskNotificationSection,
    AsyncTaskNotificationCard
  },
  data () {
    return {
      PAGE_SIZE: 100,
      LONG_INTERVAL: 4,
      SHORT_INTERVAL: 2,
      REMOVE_NOTIFICATION: true,
      // HAy que meter una variable para aplicar el filtro de las tareas
      filterByProcessing: false,
      filterByDone: false,
      filterByError: false,
      filterByCanceled: false,
      showAllNotifications: true,

      notificationSectionOpened: false,
      swingNotificationButton: false,

      taskDone: false,
      taskError: false,
      taskCanceled: false,
      // This lists render the notification cards
      currentProcessingTasks: [] as AsyncTask[],
      currentDoneTasks: [] as AsyncTask[],
      currentErrorTasks: [] as AsyncTask[],
      currentCanceledTasks: [] as AsyncTask[],
      // This maps will help to trace the time remaining between the current and the latest queried task
      // Each time some MAP is updated by 'set' is required update its corresponding list from above
      PROCESSING_TASKS_MAP: new Map<number, AsyncTask>(),
      DONE_TASKS_MAP: new Map<number, AsyncTask>(),
      ERROR_TASKS_MAP: new Map<number, AsyncTask>(),
      CANCELED_TASKS_MAP: new Map<number, AsyncTask>(),

      // if this is active, the reallocation process wait LONG_INTERVAL seconds to check notification changes
      sleepProcessingTasksReallocation: true,

      newNotifications: false
    }
  },
  async created () {
    await this.deleteExpiredStoredTasks()
    this.sincronizedAsyncTasks()
      .then(() => {
        this.loadDoneTasksFromList(this.getDoneTasksStored())
      })
  },
  async mounted () {
    this.checkTaskNotifications(this.checkChangesInProcessingTasks)
  },
  computed: {
    permissionToReadTasks (): boolean {
      return userInfo.hasPermission(PermissionNames.TASKS_READ)
    },
    permissionToCancelDatafeedTask (): boolean {
      return userInfo.hasPermission(PermissionNames.DATAFEED_DELETE)
    },
    permissionToCancelIntegrationTask (): boolean {
      return userInfo.hasPermission(PermissionNames.INTEGRATION_DELETE)
    },
    someCurrentNotification (): boolean {
      return this.currentTasksTotal > 0
    },
    processingTasksCount (): number {
      return this.currentProcessingTasks.length
    },
    doneTasksCount (): number {
      return this.currentDoneTasks.length
    },
    errorTasksCount (): number {
      return this.currentErrorTasks.length
    },
    canceledTasksCount (): number {
      return this.currentCanceledTasks.length
    },
    currentTasksTotal (): number {
      let count = 0
      if (this.showAllNotifications || this.filterByProcessing) {
        count += this.processingTasksCount
      }
      if (this.showAllNotifications || this.filterByDone) {
        count += this.doneTasksCount
      }
      if (this.showAllNotifications || this.filterByError) {
        count += this.errorTasksCount
      }
      if (this.showAllNotifications || this.filterByCanceled) {
        count += this.canceledTasksCount
      }
      return count
    },
    processingTaskListTitle (): String {
      return (this.processingTasksCount > 1
        ? 'Tareas en proceso'
        : 'Tarea en proceso') + `(${this.processingTasksCount})`
    },
    doneTaskListTitle (): String {
      return (this.doneTasksCount > 1
        ? 'Tareas finalizadas'
        : 'Tarea finalizada') + `(${this.doneTasksCount})`
    },
    errorTaskListTitle (): String {
      return (this.errorTasksCount > 1
        ? 'Tareas con error'
        : 'Tarea con error') + `(${this.errorTasksCount})`
    },
    canceledTaskListTitle (): String {
      return (this.canceledTasksCount > 1
        ? 'Tareas canceladas'
        : 'Tarea cancelada') + `(${this.canceledTasksCount})`
    },
    /** @returns true if exist some processing task notification and another with a different type */
    processingAndOther (): boolean {
      return this.processingTasksCount > 0 && (this.doneTasksCount > 0 || this.errorTasksCount > 0 || this.canceledTasksCount > 0)
    },
    /** @returns true if exist some done task notification and another with a different type */
    doneAndOther (): boolean {
      return this.doneTasksCount > 0 && (this.errorTasksCount > 0 || this.canceledTasksCount > 0 || this.processingTasksCount > 0)
    },
    /** @returns true if exist some error task notification and another with a different type */
    errorAndOther (): boolean {
      return this.errorTasksCount > 0 && (this.processingTasksCount > 0 || this.doneTasksCount > 0 || this.canceledTasksCount > 0)
    },
    /** @returns true if exist some canceled task notification and another with a different type */
    canceledAndOther (): boolean {
      return this.canceledTasksCount > 0 && (this.processingTasksCount > 0 || this.doneTasksCount > 0 || this.errorTasksCount > 0)
    },
    /** @returns true if exist at least two notifications with different type */
    atLeastTwoNotificationList (): boolean {
      return this.processingAndOther || this.doneAndOther || this.errorAndOther || this.canceledAndOther
    }
  },
  methods: {
    ...mapActions({
      reallocateProcessingTasks: 'taskNotifications/reallocateProcessingTasks',
      cancelTaskStored: 'taskNotifications/cancelTask',
      sendTaskToBlacklistStored: 'taskNotifications/blacklistTaskAsync',
      deleteExpiredStoredTasks: 'taskNotifications/deleteExpiredStoredTasks'
    }),
    ...mapMutations({
      storeTask: 'taskNotifications/addTask',
      removeTask: 'taskNotifications/removeTask'
    }),
    ...mapGetters({
      getProcessingTasksStoraged: 'taskNotifications/processingTasks',
      getDoneTasksStored: 'taskNotifications/doneTasks',
      getErrorTasksStored: 'taskNotifications/errorTasks',
      getCanceledTasksStored: 'taskNotifications/canceledTasks'
    }),
    async sincronizedAsyncTasks () {
      if (this.permissionToReadTasks) {
        const pageForWaiting = this.prepareAsyncTasksPageRequest(AsyncTaskStatus.WAITING)
        await this.loadWaitingTasks(pageForWaiting, NUMBER.ZERO)
        const pageForNew = this.prepareAsyncTasksPageRequest(AsyncTaskStatus.NEW)
        await this.loadNewTasks(pageForNew, NUMBER.ZERO)
        const pageForProcessing = this.prepareAsyncTasksPageRequest(AsyncTaskStatus.PROCESSING)
        await this.loadProcessingTasks(pageForProcessing, NUMBER.ZERO)
        this.updateCurrentProcessingTasks()

        const pageForDone = this.prepareAsyncTasksPageRequest(AsyncTaskStatus.DONE)
        await this.loadDoneTasks(pageForDone, NUMBER.ZERO)
        this.updateCurrentDoneTasks()
      }
    },
    permissionToCancelTask (task: AsyncTask) {
      if (task.type === AsyncTaskType.DATAFEED) {
        return this.permissionToCancelDatafeedTask
      } else if (task.type === AsyncTaskType.INTEGRATION) {
        return this.permissionToCancelIntegrationTask
      }
      return false
    },
    storeTaskByStatus (status: AsyncTaskStatus, task: AsyncTask) {
      this.storeTask(
        {
          taskState: status,
          task: { id: task.id, type: task.type, updateDate: task.updateDate }
        }
      )
    },
    async loadWaitingTasks (pageRequest: PageRequest<AsyncTaskFilters>, fetched: number) {
      const response = await CatalogsFillsService.getAsyncTasks(pageRequest)
      fetched += response.size
      for (const taskParams of response.results) {
        let task = AsyncTask.createInstance(taskParams)
        if (
          this.getProcessingTasksStoraged().every((currentTask: any) => currentTask.id !== task.id) &&
          this.isValidNotificationToSincronized(task)
        ) {
          this.storeTaskByStatus(task.status, task)
          this.PROCESSING_TASKS_MAP.set(task.id, task)
        }
      }
      if (fetched < response.total) {
        pageRequest.page++
        await this.loadWaitingTasks(pageRequest, fetched)
      }
    },
    async loadNewTasks (pageRequest: PageRequest<AsyncTaskFilters>, fetched: number) {
      const response = await CatalogsFillsService.getAsyncTasks(pageRequest)
      fetched += response.size
      for (const taskParams of response.results) {
        let task = AsyncTask.createInstance(taskParams)
        if (
          this.getProcessingTasksStoraged().every((currentTask: any) => currentTask.id !== task.id) &&
          this.isValidNotificationToSincronized(task)
        ) {
          this.storeTaskByStatus(task.status, task)
          this.PROCESSING_TASKS_MAP.set(task.id, task)
        }
      }
      if (fetched < response.total) {
        pageRequest.page++
        await this.loadNewTasks(pageRequest, fetched)
      }
    },
    async loadProcessingTasks (pageRequest: PageRequest<AsyncTaskFilters>, fetched: number) {
      const response = await CatalogsFillsService.getAsyncTasks(pageRequest)
      fetched += response.size
      for (const taskParams of response.results) {
        let task = AsyncTask.createInstance(taskParams)
        if (
          this.getProcessingTasksStoraged().every((currentTask: any) => currentTask.id !== task.id) &&
          this.isValidNotificationToSincronized(task)
        ) {
          this.storeTaskByStatus(task.status, task)
          this.PROCESSING_TASKS_MAP.set(task.id, task)
        }
      }
      if (fetched < response.total) {
        pageRequest.page++
        await this.loadProcessingTasks(pageRequest, fetched)
      }
    },
    async loadDoneTasks (pageRequest: PageRequest<AsyncTaskFilters>, fetched: number) {
      const response = await CatalogsFillsService.getAsyncTasks(pageRequest)
      fetched += response.size
      for (const taskParams of response.results) {
        let task = AsyncTask.createInstance(taskParams)
        if (
          this.getDoneTasksStored().every((currentTask: any) => currentTask.id !== task.id) &&
          this.isValidNotificationToSincronized(task)
        ) {
          this.storeTaskByStatus(task.status, task)
          this.DONE_TASKS_MAP.set(task.id, task)
        }
      }
      if (fetched < response.total) {
        pageRequest.page++
        await this.loadDoneTasks(pageRequest, fetched)
      }
    },
    prepareAsyncTasksPageRequest (status: AsyncTaskStatus): PageRequest<AsyncTaskFilters> {
      let pageRequest = new PageRequest<AsyncTaskFilters>()
      pageRequest.size = this.PAGE_SIZE
      pageRequest.sort = 'id'
      pageRequest.page = NUMBER.ZERO
      pageRequest.filters = {
        domain: userInfo.inRootDomain()
          ? userInfo.userInfo.domain.id
          : undefined,
        status: status
      }
      return pageRequest
    },
    isValidNotificationToSincronized (task: AsyncTask): boolean {
      return task.availableResultsLink() && !task.isTaskInBlacklistNotifications()
    },
    /**
     * To active the reactivity for the 'currentProcessingTasks' list
     */
    updateCurrentProcessingTasks () {
      this.currentProcessingTasks = this.mapToOrderedList(this.PROCESSING_TASKS_MAP)
    },
    /**
     * To active the reactivity for the 'currentDoneTasks' list
     */
    updateCurrentDoneTasks () {
      this.currentDoneTasks = this.mapToOrderedList(this.DONE_TASKS_MAP)
    },
    /**
     * To active the reactivity for the 'currentErrorTasks' list
     */
    updateCurrentErrorTasks () {
      this.currentErrorTasks = this.mapToOrderedList(this.ERROR_TASKS_MAP)
    },
    /**
     * To active the reactivity for the 'currentCanceledTasks' list
     */
    updateCurrentCanceledTasks () {
      this.currentCanceledTasks = this.mapToOrderedList(this.CANCELED_TASKS_MAP)
    },
    async checkChangesInProcessingTasks () {
      await this.reallocateProcessingTasks() // Reallocate all tasks in the store
        .then(({ inProgress, reallocated }: { inProgress: AsyncTask[], reallocated: AsyncTask[]}) => {
          if (inProgress.length === 0 && reallocated.length === 0) return

          // Update the times for each task that still in progress
          for (const taskInprogress of inProgress) {
            const oldTask = this.PROCESSING_TASKS_MAP.get(taskInprogress.id)
            if (oldTask) {
              AsyncTask.setRemainingTimeBetweenTasks(taskInprogress, oldTask)
              if (taskInprogress.type === AsyncTaskType.DATAFEED) {
                AsyncTask.setRemainingAttachmentTimeBetweenTasks(taskInprogress, oldTask)
              }
            }
            this.PROCESSING_TASKS_MAP.set(taskInprogress.id, taskInprogress)
            this.updateCurrentProcessingTasks()
          }

          // Reallocate each task from the processing task to its new state
          for (const reallocatedTask of reallocated) {
            if (reallocatedTask.status === AsyncTaskStatus.DONE) {
              this.DONE_TASKS_MAP.set(reallocatedTask.id, reallocatedTask)
              this.updateCurrentDoneTasks()
              if (reallocatedTask.type === AsyncTaskType.DATAFEED) {
                this.showDatafeedToast('Descarga finalizada', reallocatedTask.id, reallocatedTask.template.name, reallocatedTask.updateDate, reallocatedTask.process.requestId, 'success')
              } else if (reallocatedTask.type === AsyncTaskType.INTEGRATION) {
                this.showIntegrationToast('Integración finalizada', reallocatedTask.id, reallocatedTask.name, reallocatedTask.updateDate, reallocatedTask.process.requestId, 'success')
              }
            }
            if (reallocatedTask.status === AsyncTaskStatus.ERROR) {
              this.ERROR_TASKS_MAP.set(reallocatedTask.id, reallocatedTask)
              this.updateCurrentErrorTasks()
              if (reallocatedTask.type === AsyncTaskType.DATAFEED) {
                this.showDatafeedToast('Error en descarga', reallocatedTask.id, reallocatedTask.template.name, reallocatedTask.updateDate, reallocatedTask.process.requestId, 'danger')
              } else if (reallocatedTask.type === AsyncTaskType.INTEGRATION) {
                this.showIntegrationToast('Error en integración', reallocatedTask.id, reallocatedTask.name, reallocatedTask.updateDate, reallocatedTask.process.requestId, 'danger')
              }
            }
            if (reallocatedTask.status === AsyncTaskStatus.CANCELED) {
              this.CANCELED_TASKS_MAP.set(reallocatedTask.id, reallocatedTask)
              this.updateCurrentCanceledTasks()
              if (reallocatedTask.type === AsyncTaskType.DATAFEED) {
                this.showDatafeedToast('Descarga cancelada', reallocatedTask.id, reallocatedTask.template.name, reallocatedTask.updateDate, reallocatedTask.process.requestId, 'secondary')
              } else if (reallocatedTask.type === AsyncTaskType.INTEGRATION) {
                this.showIntegrationToast('Integración cancelada', reallocatedTask.id, reallocatedTask.name, reallocatedTask.updateDate, reallocatedTask.process.requestId, 'secondary')
              }
            }
            this.PROCESSING_TASKS_MAP.delete(reallocatedTask.id)
            this.updateCurrentProcessingTasks()
          }
          if (reallocated.length > 0 && !this.notificationSectionOpened) {
            this.swingBellBtn()
            this.newNotifications = true
          }
          this.sleepProcessingTasksReallocation = this.currentProcessingTasks.length === 0
        })
    },
    showDatafeedToast (title: string, id: number, template: string, date: string, requestId: string, variant: string) {
      const dateOpts: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }
      const dateFormat = Intl.DateTimeFormat('es-MX', dateOpts).format(new Date(`${date}Z`))
      const h = this.$createElement
      const node = h(
        'div', { class: ['d-flex', 'flex-column', 'p-0'] }, [
          h('p', { class: 'm-0' }, [
            h('span', { class: ['font-weight-bold', 'mr-1'] }, `ID:`), `${id}`
          ]),
          h('p', { class: 'm-0' }, [
            h('span', { class: ['font-weight-bold', 'mr-1'] }, `Template:`), template
          ]),
          h('p', { class: 'm-0' }, [
            h('span', { class: ['font-weight-bold', 'mr-1'] }, `Fecha:`), dateFormat
          ]),
          h('p', { class: 'text small m-0' }, [
            h('span', { class: ['font-weight-bold', 'mr-1'] }, `Request:`), requestId
          ])
        ])
      this.showToast(`${title}. [Mis Tareas]`, node, variant)
    },
    showIntegrationToast (title: string, id: number, filename: string, date: string, requestId: string, variant: string) {
      const dateOpts: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }
      const dateFormat = Intl.DateTimeFormat('es-MX', dateOpts).format(new Date(`${date}Z`))
      const h = this.$createElement
      const node = h(
        'div', { class: ['d-flex', 'flex-column', 'p-0'] }, [
          h('p', { class: 'm-0' }, [
            h('span', { class: ['font-weight-bold', 'mr-1'] }, `ID:`), `${id}`
          ]),
          h('p', { class: 'm-0' }, [
            h('span', { class: ['font-weight-bold', 'mr-1'] }, `Archivo:`), filename
          ]),
          h('p', { class: 'm-0' }, [
            h('span', { class: ['font-weight-bold', 'mr-1'] }, `Fecha:`), dateFormat
          ]),
          h('p', { class: 'text small m-0' }, [
            h('span', { class: ['font-weight-bold', 'mr-1'] }, `Request:`), requestId
          ])
        ])
      this.showToast(`${title}. [Integración]`, node, variant)
    },
    showToast (title: string, node: VNode, variant: string) {
      this.$bvToast.toast([node], {
        title,
        variant,
        solid: true,
        autoHideDelay: 7500
      })
    },
    async loadDoneTasksFromList (tasks: {id: number, type: AsyncTaskStatus, updateDate: string}[]) {
      const promises = []
      for (const task of tasks) {
        const request = CatalogsFillsService.getAsyncTaskById(task.id)
        promises.push(request)
      }
      return Promise.allSettled(promises)
        .then((taskPromises) => {
          for (const taskPromise of taskPromises) {
            if (taskPromise.status === 'fulfilled') {
              const task = AsyncTask.createInstance(taskPromise.value)
              if (task.status === AsyncTaskStatus.DONE) {
                this.DONE_TASKS_MAP.set(task.id, task)
              }
            }
          }
          this.updateCurrentDoneTasks()
        })
    },
    mapToOrderedList (map: Map<number, AsyncTask>, order: string = SortDirection.DESC): Array<AsyncTask> {
      let array = Array.from(map.values())
      array = array.sort((a: AsyncTask, b: AsyncTask) => a.id >= b.id ? 1 : -1)
      return order === SortDirection.DESC ? array.reverse() : array
    },
    swingBellBtn (): void {
      if (this.swingNotificationButton) return
      this.swingNotificationButton = true
      const bellBtn = document.getElementById('notificacion-btn-bell')
      bellBtn?.classList.add('animate__swing', 'animate__pulse')
      setTimeout(() => {
        this.swingNotificationButton = false
        bellBtn?.classList.remove('animate__swing', 'animate__pulse')
      }, 1000)
    },
    /**
     * To hide and show the notification panel
     */
    showNotifications (): void {
      this.notificationSectionOpened = !this.notificationSectionOpened
      if (this.notificationSectionOpened) this.newNotifications = false
    },
    /**
     * Infinity cicle to call the checkFunction.
     * This is used to check new tasks in progress.
     * @param checkFunction - fuction to call every time after X seconds
     * */
    async checkTaskNotifications (checkFunction: Function) {
      await checkFunction()
      const seconds = this.sleepProcessingTasksReallocation ? this.LONG_INTERVAL : this.SHORT_INTERVAL
      setTimeout(() => {
        this.checkTaskNotifications(checkFunction)
      }, seconds * 1000)
    },
    /**
     * Delete a task from a map
     * @param taskId task id to delete
     * @param map map from where delete
     */
    deleteTaskFromMap (taskId: number, map: Map<number, AsyncTask>) {
      map.delete(taskId)
    },
    /**
     * Delete all the notifications with a final status storaged in localStorage
     */
    deleteAllNotifications () {
      for (const state of [AsyncTaskStatus.DONE, AsyncTaskStatus.ERROR, AsyncTaskStatus.CANCELED]) {
        this.deleteAllNotificationsByStatus(state)
      }
    },
    /**
     * Delete all the notifications in a specific status
     * @param state - section to remove
     */
    deleteAllNotificationsByStatus (state: AsyncTaskStatus) {
      if (state === AsyncTaskStatus.DONE) {
        const doneTasks = this.getDoneTasksStored()
        for (const task of doneTasks) this.sendTaskToBlacklist(AsyncTaskStatus.DONE, task.id, task.type, task.updateDate)
      }
      if (state === AsyncTaskStatus.ERROR) {
        const errorTasks = this.getErrorTasksStored()
        for (const task of errorTasks) this.sendTaskToBlacklist(AsyncTaskStatus.ERROR, task.id, task.type, task.updateDate)
      }
      if (state === AsyncTaskStatus.CANCELED) {
        const canceledTasks = this.getCanceledTasksStored()
        for (const task of canceledTasks) this.sendTaskToBlacklist(AsyncTaskStatus.CANCELED, task.id, task.type, task.updateDate)
      }
      this.filterNotifications('ALL')
    },
    /**
     * Turn on/off the flag to show the corresponding notifications
     * This works as a filter
     *
     * @param section of the notifications
     */
    filterNotifications (section: string): void {
      if (section === AsyncTaskStatus.PROCESSING) {
        this.showAllNotifications = false
        this.filterByProcessing = !this.filterByProcessing
      }
      if (section === AsyncTaskStatus.DONE) {
        this.showAllNotifications = false
        this.filterByDone = !this.filterByDone
      }
      if (section === AsyncTaskStatus.ERROR) {
        this.showAllNotifications = false
        this.filterByError = !this.filterByError
      }
      if (section === AsyncTaskStatus.CANCELED) {
        this.showAllNotifications = false
        this.filterByCanceled = !this.filterByCanceled
      }
      const filters = [this.filterByProcessing, this.filterByDone, this.filterByError, this.filterByCanceled]
      if (section === 'ALL' || filters.every(filter => !filter)) {
        this.showAllNotifications = true
        this.filterByProcessing = false
        this.filterByDone = false
        this.filterByError = false
        this.filterByCanceled = false
      }
    },
    async cancelTaskById (taskId: number): Promise<void> {
      const taskToCancel = this.PROCESSING_TASKS_MAP.get(taskId)
      if (taskToCancel !== undefined) {
        await CatalogsFillsService.deleteTask(taskId)
          .then((response) => {
            LogService.warn(`Tarea ${response.id} cancelada manualmente.`)
          })
          .catch(() => {
            LogService.error(`Tarea ${taskId} no pudo ser cancelada.`)
          })
      }
    },
    /**
     * Send a task to the 'BLACKLIST' section (this tasks will not be display anymore)
     * Both are update storage and current list
     */
    sendTaskToBlacklist (state: AsyncTaskStatus, id: number, type: AsyncTaskType, updateDate: string) {
      this.sendTaskToBlacklistStored({
        taskState: state,
        task: { id, type, updateDate }
      })
      if (state === AsyncTaskStatus.DONE) {
        this.deleteTaskFromMap(id, this.DONE_TASKS_MAP)
        this.updateCurrentDoneTasks()
      }
      if (state === AsyncTaskStatus.ERROR) {
        this.deleteTaskFromMap(id, this.ERROR_TASKS_MAP)
        this.updateCurrentErrorTasks()
      }
      if (state === AsyncTaskStatus.CANCELED) {
        this.deleteTaskFromMap(id, this.CANCELED_TASKS_MAP)
        this.updateCurrentCanceledTasks()
      }
    }
  }
})
